import {useNavigate} from 'react-router-dom';

import {useQueryClient} from '@tanstack/react-query';
import {differenceInHours, parse} from 'date-fns';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {DateFormatEnum, MyAppointmentsEnum} from 'presentation/constants';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {BookingTypeEnum} from 'presentation/pages/private/booking/constants';
import {useCancelBooking} from 'presentation/pages/private/booking/hooks/useCancelBooking';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {MIN_HOURS_TO_CANCEL_SURGERY} from '../../constants';
import {AppointmentsHomeStateType} from 'presentation/pages/private/home/types';

const {cancelAppointmentModal, cancelErrorAlert, canceledAppointmentAlert} =
	translations['pt-br'].privateFlow.myAppointments;

export function useCancelBookingButton(appointment: AppointmentsHomeStateType) {
	const {isMobile} = useMediaQueries();
	const bookingData = useBookingStore(state => state.bookingData);
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const {bookingNumber, bookingType, date} = appointment;
	const isExam = bookingType === BookingTypeEnum.EXAM;
	const isSurgery = bookingType === BookingTypeEnum.SURGERY;
	const isOdonto = bookingType === BookingTypeEnum.ODONTO;

	const referenceDate = new Date();
	const appointmentDate = parse(date, DateFormatEnum.DATE_FORMAT_1, new Date());

	const showCancelButton =
		!isSurgery ||
		(isSurgery &&
			differenceInHours(appointmentDate, referenceDate) >= MIN_HOURS_TO_CANCEL_SURGERY);

	const {
		cancelExam,
		cancelHealth,
		cancelOdonto,
		isDone,
		isError,
		isPending,
		isSubmitting,
		setCancelState,
		cancelSurgery,
	} = useCancelBooking();

	const handleCancelBooking = async () => {
		if (isOdonto) {
			await cancelOdonto(bookingNumber);
			return;
		}

		if (isExam) {
			await cancelExam(appointment.bookingNumber);
			return;
		}

		if (isSurgery) {
			await cancelSurgery(Number(appointment.bookingNumber));
			return;
		}

		await cancelHealth(bookingNumber);
	};

	const handleCloseSuccessDialogState = async () => {
		setCancelState(null);
		queryClient.resetQueries({
			queryKey: [QUERY_KEYS.HOME],
		});
		navigate(MyAppointmentsEnum.MY_APPOINTMENTS);
	};

	const actionButtonText = () => {
		if (isExam) return cancelAppointmentModal.titleExam;
		if (isSurgery) return cancelAppointmentModal.titleSurgery;
		return cancelAppointmentModal.title;
	};

	const errorBookingDialogTitle = () => {
		if (isExam) return cancelErrorAlert.titleExam;
		if (isSurgery) return cancelErrorAlert.titleSurgery;
		return cancelErrorAlert.title;
	};

	const successBookingDialogHeaderTitle = () => {
		if (isExam) return canceledAppointmentAlert.headerTitleExam;
		if (isSurgery) return canceledAppointmentAlert.headerTitleSurgery;
		return canceledAppointmentAlert.headerTitle;
	};

	const successBookingDialogTitle = () => {
		if (isExam) return canceledAppointmentAlert.titleExam;
		if (isSurgery) return canceledAppointmentAlert.titleSurgery;
		return canceledAppointmentAlert.title;
	};

	return {
		showCancelButton,
		isMobile,
		isSurgery,
		isDone,
		isError,
		isPending,
		isSubmitting,
		actionButtonText,
		errorBookingDialogTitle,
		successBookingDialogHeaderTitle,
		successBookingDialogTitle,
		handleCancelBooking,
		handleCloseSuccessDialogState,
		bookingData,
		setCancelState,
		bookingType,
	};
}
